import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    Typography
} from '@mui/material';
import { headingBlue, red, maroon } from '../../assets/colors';
import { buttonStyle, commonErrorStyleText } from '../../utils/commonCSS';
import CustomTextField from '../CustomTextField';
import CustomDropDown from '../CustomDropDown';
import HttpService from '../../backend/requestApis';
import { fetchStatesForHead, fetchDistrictsForHead, generateAnInvite } from '../../backend/backendApis';
import Loader from '../Loader';
import { useNavigate } from 'react-router-dom';

const GenerateInviteDialog = (props) => {
    const navigate = useNavigate();

    const [name, setName] = useState('');
    const [contactNumber, setContactNumber] = useState('');
    const [selectedState, setSelectedState] = useState('State *');
    const [selectedDistrict, setSelectedDistrict] = useState('District *');
    const [stateList, setStateList] = useState([]);
    const [districtsList, setDistrictsList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [apiError, setApiError] = useState('');
    const [nameError, setNameError] = useState('');
    const [contactNumberError, setContactNumberError] = useState('');
    const [stateError, setStateError] = useState('');
    const [districtError, setDistrictError] = useState('');

    const titleStyle = {
        backgroundColor: headingBlue,
        color: 'white',
        fontSize: '1rem',
        display: "flex",
        alignItems: 'center',
        padding: "0.4rem 0rem 0.4rem 1rem"
    };

    useEffect(() => {
        if (props.dialogStatus) {
            // Dialog is opening, fetch states
            const fetchStates = async () => {
                try {
                    setIsLoading(true);
                    setApiError(''); // Clear any previous errors
                    const options = {
                        url: fetchStatesForHead,
                        method: 'GET',
                        headers: {
                            Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null,
                        },
                    };
                    const response = await HttpService(options);
                    if (response.data.status === 1) {
                        setStateList(response.data.data);
                    } else {
                        setApiError(response.data.message || 'Error fetching states.');
                    }
                } catch (err) {
                    console.error('Error in fetching all states:', err);
                    if (err?.response?.status === 403) {
                        localStorage.clear();
                        window.location.href = '/Login';
                        return;
                    } else {
                        setApiError('Error in fetching states. Please contact support.');
                    }
                } finally {
                    setIsLoading(false);
                }
            };
            fetchStates();
        } else {
            // Dialog is closing, reset state
            setName('');
            setContactNumber('');
            setSelectedState('State *');
            setSelectedDistrict('District *');
            setDistrictsList([]);
            setNameError('');
            setContactNumberError('');
            setStateError('');
            setDistrictError('');
            setApiError('');
        }
    }, [props.dialogStatus, navigate]);

    useEffect(() => {
        const fetchDistricts = async () => {
            if (selectedState && selectedState !== 'State *') {
                try {
                    setIsLoading(true);
                    setApiError('');
                    const options = {
                        url: fetchDistrictsForHead,
                        method: 'GET',
                        params: {
                            stateFilter: selectedState
                        },
                        headers: {
                            Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null,
                        },
                    };
                    const response = await HttpService(options);
                    setDistrictsList(response?.data?.data || []);
                } catch (err) {
                    console.error('Error in fetching districts:', err);
                    if (err?.response?.status === 403 || err?.status === 403) {
                        localStorage.clear();
                        window.location.href = '/Login';
                        return;
                    } else {
                        setApiError('Error in fetching districts. Please contact support.');
                    }
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchDistricts();
    }, [selectedState, navigate]);

    const validateGenerate = () => {
        let isValid = true;
        
        if (!name) {
            setNameError('Name is required');
            isValid = false;
        } else {
            setNameError('');
        }
        
        if (!contactNumber) {
            setContactNumberError('Contact Number is required');
            isValid = false;
        } else {
            setContactNumberError('');
        }
        
        if (!selectedState || selectedState === 'State *') {
            setStateError('State is required');
            isValid = false;
        } else {
            setStateError('');
        }
        
        if (!selectedDistrict || selectedDistrict === 'District *') {
            setDistrictError('District is required');
            isValid = false;
        } else {
            setDistrictError('');
        }
        
        return isValid;
    };

    const handleGenerateInvite = () => {
        let validationResult = validateGenerate();

        if (validationResult) {
            setIsLoading(true);
            setApiError('');
            let options = {
                url: generateAnInvite,
                method: 'POST',
                payload: { name, contactNumber, state: selectedState, district: selectedDistrict },
                headers: {
                    headers: {
                        Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : ''
                    }
                }
            };

            HttpService(options).then((response) => {
                setIsLoading(false);
                if (response?.data?.status === 1) {
                    props.updateDialogStatus(false, true);
                } else {
                    setApiError(response?.data?.message || 'Failed to generate invite');
                }
            }).catch(err => {
                setIsLoading(false);
                if (err?.response?.status === 403 || err?.status === 403) {
                    localStorage.clear();
                    window.location.href = '/Login';
                    return;
                } else {
                    setApiError(err?.response?.data?.message || 'An error occurred while generating invite.');
                }
            });
        }
    };

    return (
        <Dialog
            maxWidth="sm"
            fullWidth
            open={props.dialogStatus}
            onClose={() => props.updateDialogStatus(false)}
        >
            <DialogTitle style={titleStyle}>
                Generate Invite
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}> {/* Remove default padding from DialogContent */}
                <Box sx={{ pt: 4 }}>
                    {isLoading && <Loader />}
                    <Grid container spacing={1}>
                        <Grid item xs={6} sm={6}>
                            <CustomTextField placeHolder="Name *" value={name} setFunc={(value) => setName(value)} regular={true} />
                            {nameError && <Typography sx={{ ...commonErrorStyleText, color: red, fontSize: '0.85rem', textAlign: 'left' }}>{nameError}</Typography>}
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <CustomTextField placeHolder="Contact Number *" value={contactNumber} setFunc={(value) => setContactNumber(value)} regular={true}/>
                            {contactNumberError && <Typography sx={{ ...commonErrorStyleText, color: red, fontSize: '0.85rem', textAlign: 'left' }}>{contactNumberError}</Typography>}
                        </Grid> 
                        <Grid item xs={12} sm={6}> {/* Changed to 12 for better mobile layout, can be 6 if preferred */}
                            {/* Pass fetched stateList to CustomDropDown */}
                            <CustomDropDown placeHolder="State *" list={stateList} value={selectedState} setFunc={(value) => setSelectedState(value)} 
                                // regular={true} width='13rem'
                                />
                            {stateError && <Typography sx={{ ...commonErrorStyleText, color: red, fontSize: '0.85rem', textAlign: 'left' }}>{stateError}</Typography>}
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <CustomDropDown placeHolder="District *" list={districtsList} value={selectedDistrict} setFunc={(value) => setSelectedDistrict(value)} />
                            {districtError && <Typography sx={{ ...commonErrorStyleText, color: red, fontSize: '0.85rem', textAlign: 'left' }}>{districtError}</Typography>}
                        </Grid>
                    </Grid>
                </Box>
                <Box style={{ paddingTop: '2rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                    <Button style={{ ...buttonStyle, backgroundColor: red }} onClick={() => props.updateDialogStatus(false)}>
                        Cancel
                    </Button>
                    <Button style={buttonStyle} onClick={handleGenerateInvite}>
                        Generate
                    </Button>
                </Box>
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                    {apiError && <Typography sx={{ ...commonErrorStyleText, color: red, fontSize: '0.85rem' }}>{apiError}</Typography>}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default GenerateInviteDialog;